import "./gallery.scss"
import { React, useState, useEffect } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

import ReactPlayer from 'react-player';
import { useLocation, useNavigate } from 'react-router-dom';
import { logAnalyticsEvent } from '../../services/firebase';

// Cloudinary transform helper: insert resize params into a Cloudinary URL for optimized loading
const cldOptimize = (url, w = 1200, crop = 'limit') => {
    if (url && url.includes('res.cloudinary.com') && url.includes('/upload/')) {
        return url.replace('/upload/', `/upload/w_${w},c_${crop},f_auto,q_auto/`);
    }
    return url;
};

const Gallery = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const url = location.pathname;
    const project = url.split('/').at(-1);

    const [model, setModel] = useState(false);
    const [tempImgIndex, setTempImgIndex] = useState(0);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadedGridImages, setLoadedGridImages] = useState({});
    const [modalLoading, setModalLoading] = useState(true);
    const [headerImage, setHeaderImage] = useState("");

    const registerView = async (item) => {
        const userId = localStorage.getItem('userId') || 'Anonymous';
        if (!["/dashboard"].includes(url)) {
            await logAnalyticsEvent('image_view', item, userId);
        }
    };

    const closeImg = () => { setModel(false); setTempImgIndex(0); };

    const preloadAdjacents = (currentIndex) => {
        if (!images || images.length === 0) return;
        const nextId = (currentIndex + 1) % images.length;
        let prevId = currentIndex - 1;
        if (prevId < 0) prevId += images.length;

        if (images[nextId]?.type !== 'video' && images[nextId]?.url) {
            const img = new Image();
            img.src = images[nextId].url;
        }
        if (images[prevId]?.type !== 'video' && images[prevId]?.url) {
            const img = new Image();
            img.src = images[prevId].url;
        }
    };

    const getImg = (index) => {
        // Only show loading spinner if the image index actually changes.
        // When tempImgIndex is already equal to index (e.g. clicking the 1st image
        // when tempImgIndex starts at 0), the <img> src won't change and onLoad
        // won't re-fire, so modalLoading would stay true forever (black screen).
        if (index !== tempImgIndex) {
            setModalLoading(true);
        } else {
            setModalLoading(false);
        }
        setTempImgIndex(index);
        setModel(true);
        registerView(project + "-" + index);
        preloadAdjacents(index);
    };

    const getOther = (offset) => {
        let newID = tempImgIndex + offset;
        if (newID >= images.length) newID -= images.length;
        if (newID < 0) newID += images.length;
        setModalLoading(true);
        setTempImgIndex(newID);
        registerView(project + "-" + newID);
        preloadAdjacents(newID);
    };

    document.onkeydown = (event) => {
        if (model) {
            if (event.key === "Escape") closeImg();
            else if (event.key === "ArrowLeft") getOther(-1);
            else if (event.key === "ArrowRight") getOther(1);
        }
    };

    useEffect(() => {
        const fetchProjectData = async () => {
            setImages([]);
            setHeaderImage("");
            setLoadedGridImages({});
            setLoading(true);
            try {
                const password = localStorage.getItem('password') || "";
                const res = await fetch("/api/getProjectData", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ password, projectUrl: url })
                });

                if (!res.ok) {
                    navigate('/home', { replace: true });
                    return;
                }

                const projectData = await res.json();
                const mediaItems = projectData.media || [];

                const loaded = mediaItems
                    .filter(item => typeof item === 'object' && item.url)
                    .map(item => ({
                        type: item.type || 'image',
                        url: item.url ? item.url.replace(/["']/g, '').trim() : '',
                        thumbnail: item.thumbnail ? item.thumbnail.replace(/["']/g, '').trim() : '',
                        description: item.description || ''
                    }));

                setImages(loaded);
                setHeaderImage(projectData.header_image || "");
            } catch (err) {
                console.error("Error fetching project:", err);
                navigate('/home', { replace: true });
            }
            setLoading(false);
        };
        fetchProjectData();
    }, [url, navigate]);

    if (loading) return <div className="galleryContainer pageLoader"><div className="spinner" style={{ position: 'relative', top: 'unset', left: 'unset', marginTop: 0, marginLeft: 0 }}></div></div>;

    if (images.length > 0) {
        const current = images[tempImgIndex];
        return (
            <>
                {/* ─── Fullscreen Overlay ─── */}
                <div className={model ? "model open" : "model"}>
                    <div className="buttons">
                        <CloseRoundedIcon className="close" onClick={closeImg} />
                        <ArrowBackIosNewRoundedIcon className="back" onClick={() => getOther(-1)} />
                        <ArrowForwardIosRoundedIcon className="forward" onClick={() => getOther(1)} />
                    </div>
                    <div className={model ? "mainImg open" : "mainImg"}>
                        {(modalLoading) && <div className="spinner"></div>}

                        {current.type === 'video' ? (
                            <div className="video-player-container" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div className="video-wrapper">
                                    <ReactPlayer
                                        url={current.url}
                                        controls
                                        width="100%"
                                        height="100%"
                                        className="react-player"
                                        playing={model}
                                        onReady={() => setModalLoading(false)}
                                        onError={(e) => {
                                            console.error("ReactPlayer Error:", e, current.url);
                                            setModalLoading(false);
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <img className="img" src={cldOptimize(current.url, 2000)} alt={current.description}
                                onLoad={() => setModalLoading(false)}
                                style={{ opacity: modalLoading ? 0 : 1, transition: 'opacity 0.4s ease' }} />
                        )}
                        {current.description && !modalLoading && (
                            <label className="description">{current.description}</label>
                        )}
                    </div>
                    <div className="bg" onClick={closeImg}></div>
                </div>

                {/* ─── Grid ─── */}
                <div className="galleryContainer fadeIn">
                    <div className="galleryTitle">
                        {headerImage && <img className="logoProject" src={headerImage} alt="Project Header" />}
                    </div>
                    <ul className="gallery">
                        {images.map((item, index) => (
                            <li className={`imgFrame ${item.type === 'video' ? 'videoFrame' : ''}`} key={index} onClick={() => getImg(index)}>
                                {(!loadedGridImages[index] && (item.type !== 'video' || item.thumbnail)) && <div className="skeleton"></div>}
                                {item.type === 'video' ? (
                                    item.thumbnail ? (
                                        <img className="img" src={cldOptimize(item.thumbnail, 600, 'fill')} alt={item.description}
                                            onLoad={() => setLoadedGridImages(prev => ({ ...prev, [index]: true }))}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: loadedGridImages[index] ? 1 : 0 }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
                                            <ReactPlayer url={item.url} light={true} width="100%" height="100%" />
                                        </div>
                                    )
                                ) : (
                                    <img className="img" src={cldOptimize(item.url, 1200, 'fill')} alt={item.description}
                                        onLoad={() => setLoadedGridImages(prev => ({ ...prev, [index]: true }))}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: loadedGridImages[index] ? 1 : 0 }} />
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </>
        );
    }

    return <div className="galleryContainer"><h1 style={{ color: "white", textAlign: "center", marginTop: "50px" }}>No images found for this project.</h1></div>;
};

export default Gallery;